import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserEarnings, updateUserStatus, getUserProfile } from '../utils/api';
import io from 'socket.io-client';
import { notify } from '../utils/notify';
import './FemaleDashboard.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function FemaleDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [earnings, setEarnings] = useState(0);
    const [calls, setCalls] = useState([]);
    // Initialize isOnline from user object if available, otherwise from server
    const [isOnline, setIsOnline] = useState(user?.isOnline || false);
    const [socket, setSocket] = useState(null);
    const [incomingCall, setIncomingCall] = useState(null);

    // Load user's current online status from server when component mounts
    // If user is approved but offline, automatically set them online
    useEffect(() => {
        const loadUserStatus = async () => {
            if (!user?.id) return;
            try {
                const profile = await getUserProfile(user.id);
                if (profile) {
                    // If user is approved but offline, automatically set them online
                    if (profile.isApproved && profile.status === 'approved' && !profile.isOnline) {
                        console.log('✅ User is approved but offline, setting online automatically...');
                        setIsOnline(true);
                        await updateUserStatus(user.id, true, false);
                        // Socket will emit user-online when it connects (handled in socket useEffect)
                    } else if (profile.isOnline !== undefined) {
                        setIsOnline(profile.isOnline);
                        console.log('📡 Loaded user online status from server:', profile.isOnline);
                    }
                }
            } catch (error) {
                console.error('Error loading user status:', error);
                // Fallback: if user is approved, set online automatically
                if (user.isApproved && user.status === 'approved' && !user.isOnline) {
                    console.log('✅ Fallback: User is approved but offline, setting online automatically...');
                    setIsOnline(true);
                    updateUserStatus(user.id, true, false).catch(err => console.error('Error updating status:', err));
                } else if (user.isOnline !== undefined) {
                    setIsOnline(user.isOnline);
                }
            }
        };
        loadUserStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    useEffect(() => {
        loadEarnings();
        const newSocket = io(SOCKET_URL, { auth: { userId: user.id } });
        setSocket(newSocket);

        // Wait for socket connection before emitting
        newSocket.on('connect', async () => {
            console.log('Socket connected:', newSocket.id);
            // Check current online status and emit if online
            try {
                const profile = await getUserProfile(user?.id);
                if (profile && profile.isApproved && profile.status === 'approved') {
                    // If user is approved but offline, set online automatically
                    if (!profile.isOnline) {
                        console.log('✅ User is approved but offline, setting online automatically on socket connect...');
                        setIsOnline(true);
                        await updateUserStatus(user.id, true, false);
                    }
                    // Emit user-online if online
                    if (profile.isOnline || isOnline) {
                        newSocket.emit('user-online', user.id);
                        console.log('✅ Emitted user-online on socket connect:', user.id);
                    }
                }
            } catch (error) {
                console.error('Error checking user status on socket connect:', error);
                // Fallback: emit if isOnline state is true
                if (isOnline) {
                    newSocket.emit('user-online', user.id);
                    console.log('✅ Emitted user-online (fallback) on socket connect:', user.id);
                }
            }
        });

        newSocket.on('incoming-call', (data) => {
            // Handle incoming call
            console.log('Incoming call received:', data);
            // If this call was broadcasted, ignore calls not intended for this user
            if (data?.receiverId && data.receiverId !== user?.id) {
                return;
            }
            setIncomingCall(data);
            // Store signal in localStorage for VideoCall page
            if (data.signal) {
                localStorage.setItem('incomingCallSignal', JSON.stringify(data.signal));
                localStorage.setItem('incomingCallerId', data.callerId);
            }
        });

        newSocket.on('call-ended', (data) => {
            // Handle call ended by caller
            console.log('Call ended by caller:', data);
            setIncomingCall(null);
        });

        return () => {
            // IMPORTANT: Don't automatically set user offline when navigating to VideoCall
            // Only disconnect socket - user controls their online/offline status manually
            // The online status should only change when user explicitly toggles it or logs out
            newSocket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // Emit user-online/user-offline when isOnline status changes or socket connects
    useEffect(() => {
        if (!socket || !user?.id) return;
        
        // Only emit if socket is connected
        if (socket.connected) {
            if (isOnline) {
                socket.emit('user-online', user.id);
                console.log('✅ Emitted user-online:', user.id, 'isOnline:', isOnline);
            } else {
                socket.emit('user-offline', user.id);
                console.log('❌ Emitted user-offline:', user.id, 'isOnline:', isOnline);
            }
        } else {
            // Wait for socket to connect, then emit
            socket.once('connect', () => {
                if (isOnline) {
                    socket.emit('user-online', user.id);
                    console.log('✅ Emitted user-online after connect:', user.id);
                }
            });
        }
    }, [socket, user?.id, isOnline]);

    const loadEarnings = async () => {
        try {
            const data = await getUserEarnings(user.id);
            setEarnings(data.totalEarnings);
            setCalls(data.calls);
        } catch (error) {
            console.error('Error loading earnings:', error);
        }
    };

    const toggleOnlineStatus = async () => {
        const newStatus = !isOnline;
        setIsOnline(newStatus);
        await updateUserStatus(user.id, newStatus, false);
        
        if (newStatus && socket) {
            // Emit user-online to register in Socket.IO
            socket.emit('user-online', user.id);
            console.log('User online emitted:', user.id);
        } else if (!newStatus && socket) {
            socket.emit('user-offline', user.id);
        }
    };

    const handleAcceptCall = () => {
        if (incomingCall && socket) {
            // Accept the call by sending signal back
            // First, get user media
            navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                .then(() => {
                    // Navigate to video call page with caller ID
                    navigate(`/video-call/${incomingCall.callerId}`);
                })
                .catch(err => {
                    console.error('Failed to get media:', err);
                    notify.error('Permission required', 'Please allow camera and microphone access.');
                });
        }
    };

    const handleRejectCall = () => {
        if (socket && incomingCall) {
            // Notify caller that call was rejected
            socket.emit('reject-call', { callerId: incomingCall.callerId, receiverId: user.id });
        }
        setIncomingCall(null);
    };

    const handleLogout = async () => {
        // Set user offline before logout
        try {
            await updateUserStatus(user.id, false, false);
            if (socket) {
                socket.emit('user-offline', user.id);
                socket.disconnect();
            }
        } catch (error) {
            console.error('Error setting user offline:', error);
        }
        logout();
    };

    return (
        <div className="female-dashboard">
            <header>
                <h1>Hello, {user?.nickname || 'Model'}</h1>
                <button 
                    className="btn-edit-profile"
                    onClick={() => navigate('/edit-profile')}
                >
                    Edit Profile
                </button>
                <button className="btn-logout" onClick={handleLogout}>Logout</button>
            </header>
            
            <main>
                <div className="status-section">
                    <h2>Your Status</h2>
                    <div className="toggle-container">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={isOnline}
                                onChange={toggleOnlineStatus}
                            />
                            <span className="toggle-slider">
                                <span className="toggle-label">{isOnline ? 'ON' : 'OFF'}</span>
                            </span>
                        </label>
                        <span className="status-text">{isOnline ? 'Online' : 'Offline'}</span>
                    </div>
                </div>

                <div className="earnings-section">
                    <h2>Total Earnings</h2>
                    <p className="earnings-amount">${earnings.toFixed(2)}</p>
                    <p>Rate: ${user?.pricePerMinute}/minute</p>
                    <button 
                        className="btn-payments"
                        onClick={() => navigate('/payments')}
                    >
                        💰 Payments & Payouts
                    </button>
                </div>

                <div className="calls-section">
                    <h2>Call History</h2>
                    {calls.length === 0 ? (
                        <p>No calls yet</p>
                    ) : (
                        <div className="calls-list">
                            {calls.map(call => (
                                <div key={call._id} className="call-item">
                                    <p>With: {call.caller?.nickname}</p>
                                    <p>Duration: {Math.floor(call.duration / 60)} min</p>
                                    <p>Earned: ${call.earnings.toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Incoming Call Modal */}
            {incomingCall && (
                <div className="incoming-call-modal">
                    <div className="incoming-call-content">
                        <div className="caller-avatar">
                            <img 
                                src={incomingCall.callerPhoto 
                                    ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}/${incomingCall.callerPhoto}`
                                    : 'https://via.placeholder.com/150'
                                }
                                alt={incomingCall.callerName}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/150';
                                }}
                            />
                            <div className="ringing-indicator">
                                <div className="ring-ring"></div>
                                <div className="ring-ring"></div>
                                <div className="ring-ring"></div>
                            </div>
                        </div>
                        <h2 className="caller-name">{incomingCall.callerName || 'Unknown'}</h2>
                        <p className="call-status-text">Incoming Video Call</p>
                        <div className="call-actions">
                            <button 
                                className="btn-accept"
                                onClick={handleAcceptCall}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" fill="currentColor"/>
                                </svg>
                                Accept
                            </button>
                            <button 
                                className="btn-reject"
                                onClick={handleRejectCall}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" fill="currentColor"/>
                                </svg>
                                Decline
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FemaleDashboard;
